import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Event controller for bundle UI listeners.
   * @type {AbortController | null}
   */
  #bundleController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    this.#setupBundleButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#bundleController?.abort();
  }

  #setupBundleButton() {
    this.#bundleController?.abort();
    this.#bundleController = new AbortController();

    const { signal } = this.#bundleController;
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#updateBundleSummary, { signal });
    this.querySelector('[data-bundle-add-button]')?.addEventListener('click', this.#addBundleToCart, { signal });
    this.#updateBundleSummary();
  }

  /**
   * Gets the selected bundle item quantities grouped by variant id.
   * @returns {Array<{id: number, quantity: number}>}
   */
  #getBundleItems() {
    const itemsByVariant = new Map();

    for (const item of this.querySelectorAll('[data-bundle-item]')) {
      if (!(item instanceof HTMLElement)) continue;

      const variantId = Number(item.dataset.variantId);
      if (!variantId) continue;

      const quantityInput = /** @type {HTMLInputElement | null} */ (item.querySelector('input[name="quantity"]'));
      const quantity = Math.max(0, Number(quantityInput?.value) || 0);
      if (quantity === 0) continue;

      itemsByVariant.set(variantId, (itemsByVariant.get(variantId) || 0) + quantity);
    }

    return Array.from(itemsByVariant, ([id, quantity]) => ({ id, quantity }));
  }

  #updateBundleSummary = () => {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-bundle-add-button]'));
    if (!button) return;

    let totalQuantity = 0;
    let totalPrice = 0;
    let totalComparePrice = 0;

    for (const item of this.querySelectorAll('[data-bundle-item]')) {
      if (!(item instanceof HTMLElement)) continue;

      const quantityInput = /** @type {HTMLInputElement | null} */ (item.querySelector('input[name="quantity"]'));
      const quantity = Math.max(0, Number(quantityInput?.value) || 0);
      const price = Number(item.dataset.price) || 0;
      const comparePrice = Number(item.dataset.comparePrice) || price;

      totalQuantity += quantity;
      totalPrice += price * quantity;
      totalComparePrice += comparePrice * quantity;
    }

    button.disabled = totalQuantity === 0;

    const count = button.querySelector('.bundle-count');
    const finalPrice = button.querySelector('.final-price');
    const comparePrice = button.querySelector('.compare-price');

    if (count) count.textContent = totalQuantity.toString();
    if (finalPrice) finalPrice.textContent = this.#formatMoney(totalPrice);
    if (comparePrice) {
      comparePrice.textContent = this.#formatMoney(totalComparePrice);
      comparePrice.toggleAttribute('hidden', totalComparePrice <= totalPrice);
    }
  };

  #formatMoney(value) {
    return formatMoney(value, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }

  #addBundleToCart = async () => {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-bundle-add-button]'));
    const items = this.#getBundleItems();
    if (!button || items.length === 0) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.dataset.added = 'true';

    try {
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sections = [];

      for (const item of cartItemsComponents) {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sections.push(item.dataset.sectionId);
        }
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      window.setTimeout(() => {
        button.removeAttribute('data-added');
      }, 100);
      const data = await response.json();

      if (!response.ok || data.status) {
        const message = data.message || 'Unable to add bundle to cart';
        this.dispatchEvent(new CartErrorEvent(this.id, message, data.description, data.errors));
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

      this.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'product-recommendations-bundle',
          itemCount,
          sections: data.sections,
        })
      );

    } catch (error) {
      console.error('Product recommendations bundle error:', error);
    } finally {
      button.removeAttribute('aria-busy');
      this.#updateBundleSummary();
    }
  };

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
          this.#setupBundleButton();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;

      /* Product_recommendations_slider */
      document.querySelectorAll('.Product_recommendations_slider').forEach(el => {
        if (!el.classList.contains('swiper-initialized')) {
          new Swiper(el, {
            loop: false,
            grabCursor: true,
            spaceBetween: 16,
            slidesPerGroup: 1,
            slidesPerView: "auto",
            watchSlidesProgress: true,
            draggable: !0,
            autoHeight: !1,
            watchOverflow: !0,
            threshold: 10,
            mousewheel: {
                forceToAxis: !0
            },
            observer: true,
            observeParents: true,
            freeMode: !0,
            breakpoints: {
                0: {
                    freeMode: !1
                },
                750: {
                },
                990: {
                },
                1200: {
                },
                1440: {
                }
            }
          })
        }
      });
      /* Product_recommendations_slider */
      
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
